<template>
  <v-col cols="12" md="6" class="pl-1 pt-0">
    <v-card class="d-flex fill-height flex-column">
      <div class="d-flex align-center flex-wrap pt-2 px-2">
        <v-tabs class="d-flex tabs-class flex-grow-0" v-model="activeTab" style="width: auto;">
          <v-tab>{{ $t('broker.activePositions.title') }}</v-tab>
          <v-tab>{{ $t('broker.activePositions.activeOperations') }}</v-tab>
        </v-tabs>
        <v-btn small color="primary" class="ml-4" @click="$emit('open-order-log')">
          {{ $t('broker.activePositions.orderLog') }}
        </v-btn>
        <v-spacer></v-spacer>

        <div class="d-flex align-end mr-2">
          <span class="ml-1">
            {{ $t('broker.activePositions.volume') }}
            <span class="font-weight-bold">{{ formatCurrencyBRLCompactAbbr(activePositions.volume) }}</span>
          </span>
          <span class="mx-1">|</span>
          <span class="mr-1">
            {{ $t('broker.activePositions.revenue') }}
            <span :class="[getClassColor(activePositions.revenue), 'font-weight-bold']">{{
              formatCurrencyBRLCompactSigned(activePositions.revenue) }}</span>
          </span>
        </div>
      </div>

      <v-divider class="mx-2"></v-divider>

      <v-card-text class="flex-grow-1">
        <div class="active-rfqs d-flex flex-column justify-space-between">
          <v-tabs-items v-model="activeTab" class="transparent">
            <!-- TAB 0: ACTIVE RFQS -->
            <v-tab-item>
              <v-row dense style="min-height: 200px">
                <template v-if="displayedCards.length">
                  <v-col v-for="(deal, index) in displayedCards" :key="deal.rfq_id || index" cols="4" md="4" lg="4"
                    sm="6">
                    <v-sheet class="fill-height pa-4 d-flex flex-column justify-space-between flex-nowrap" rounded
                      outlined>
                      <!-- Header: Side + Pair + Amount -->

                      <div class="d-flex justify-space-between font-weight-bold mb-1 flex-nowrap">
                        <span class="text-nowrap">
                          <span v-if="deal.side" :style="deal.side === 'BUY' ? 'color:#00c853' : 'color:#ff6d00'">
                            {{ deal.side }}
                          </span>
                          <span v-if="deal.side"> &#8226; </span>
                          {{ deal.pair }} &#8226; {{ formatCurrencyBRL(deal.amount) }}
                          &#8226; {{printRate(deal) }}
                        </span>
                        <v-chip small :color="getStatusColor(deal.status)" outlined>
                          {{ translateStatus(deal.status, false) }}
                        </v-chip>
                      </div>

                      <!-- Client Name -->
                      <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                        <v-chip v-if="isBroker && deal.isPartner" small  class="partner-chip">
                          {{ deal.partnerName }}
                        </v-chip>
                        <div>{{ deal.client }}</div>
                      </div>

                      <!-- Timestamp -->
                      <div class="my-1 text-caption grey--text" v-if="deal.status === 'PENDING'">
                        {{ $t('broker.activePositions.scheduledTo') }} {{ deal.time }}
                      </div>
                      <div class="my-2 text-caption grey--text" v-else>
                        {{ deal.time }}
                      </div>

                      <!-- Action Buttons -->
                      <!-- update : add reconcile button
                      <div>
                        <v-btn small color="primary" class="mr-2 mb-3" :disabled="deal.status === 'DEAL' || true"
                          @click="$emit('execute', deal)">
                          {{ $t('instructions.reconcile') }}
                        </v-btn>
                      </div>-->
                      <div class="d-flex flex-row align-center flex-wrap flex-gap-2">
                        <v-btn v-if="isBroker" @click="openReconcileModal(deal)" :disabled="deal.status !== 'DEAL' || isReconciled(deal)" small color="primary" class="mr-2">
                      {{ $t('instructions.reconcile') }}
                        </v-btn>

                        <v-btn small color="error" :disabled="deal.status === 'DEAL' || true"
                          @click="$emit('cancel', deal)">
                          {{ $t('instructions.cancel') }}
                        </v-btn>

                        <v-spacer></v-spacer>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn small icon v-on="on" v-bind="attrs" @click="$emit('view-rfq-report', deal)">
                              <v-icon small>mdi-file-find-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('instructions.viewReport') }}</span>
                        </v-tooltip>
                      </div>
                    </v-sheet>
                  </v-col>
                </template>

                <!-- EMPTY STATE -->
                <template v-else>
                  <v-col cols="12" class="d-flex justify-center align-center grey--text" style="min-height: 340px">
                  </v-col>
                </template>
              </v-row>
            </v-tab-item>

            <!-- TAB 1: ACTIVE OPERATIONS -->
            <v-tab-item>
              <v-row dense style="min-height: 340px">
                <template v-if="displayedCards.length">
                  <v-col v-for="(op, index) in displayedCards" :key="op.id || index" cols="4" md="4" lg="4" sm="6">
                    <v-sheet class="pa-4 d-flex flex-column justify-space-between" rounded outlined>
                      <!-- Header: Alias + Status -->
                      <div class="d-flex justify-space-between font-weight-bold mb-1">
                        <span class="text-truncate mr-1" :title="op.alias" style="font-size: 0.75rem !important;">{{
                          op.alias }}</span>
                        <v-chip small :color="getStatusColor(op.status)" outlined>
                          {{ translateStatus(op.status, true) }}
                        </v-chip>
                      </div>

                      <!-- Client Name -->
                      <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                        <v-chip v-if="isBroker && op.isPartner" small  class="partner-chip">
                          {{ op.partnerName }}
                        </v-chip>
                        <div>{{ op.client }}</div>
                      </div>

                      <!-- Operation Purpose -->
                      <div class="my-1 text-caption grey--text text--lighten-1" v-if="op.operationType">
                        {{ formatOperationTypeTitle(op.operationType) }}
                      </div>

                      <!-- Timestamp -->
                      <div class="my-2 text-caption grey--text">
                        {{ op.time }}
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-flex flex-row align-center flex-wrap flex-gap-2">
                        <v-btn small color="primary" class="mr-2" :disabled="true">
                          {{ $t('instructions.execute') }}
                        </v-btn>

                        <v-btn small color="error" :disabled="true">
                          {{ $t('instructions.cancel') }}
                        </v-btn>

                        <v-spacer></v-spacer>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn small icon v-on="on" v-bind="attrs" @click="$emit('view-operation-report', op)">
                              <v-icon small>mdi-file-find-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('instructions.viewReport') }}</span>
                        </v-tooltip>
                      </div>
                    </v-sheet>
                  </v-col>
                </template>

                <!-- EMPTY STATE -->
                <template v-else>
                  <v-col cols="12" class="d-flex justify-center align-center grey--text" style="min-height: 340px">
                  </v-col>
                </template>
              </v-row>
            </v-tab-item>
          </v-tabs-items>

          <v-pagination v-if="totalCardsCount > activePositionsCardsPerPage" v-model="currentActivePositionsPage"
            :length="Math.max(1, Math.ceil(totalCardsCount / activePositionsCardsPerPage))" circle color="primary"
            class="my-2" />
        </div>
      </v-card-text>
    </v-card>
    <the-rfq-window
      v-model="reconcileModal"
      mode="reconciliation"
      :rfq-to-manual-price="reconcileDealData"
      @reconcileSubmit="handleReconcileSubmit"
    />
  </v-col>
</template>

<script>
import TheRfqWindow from '../modal/TheRfqWindow.vue';

export default {
  name: 'ActivePositionsPanel',
  components:{TheRfqWindow},
  props: {
    activePositions: {
      type: Object,
      default: () => ({ volume: 0, revenue: 0, fees: 0 })
    },
    rfqCards: {
      type: Array,
      default: () => []
    },
    operationCards: {
      type: Array,
      default: () => []
    },
    isBroker: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 0,
      currentActivePositionsPage: 1,
      activePositionsCardsPerPage: 6,
      reconcileModal: false,
      selectedDeal: null,
    }
  },
  computed: {
    displayedCards() {
      const per = this.activePositionsCardsPerPage || 1
      const cards = this.activeTab === 0 ? this.rfqCards : this.operationCards
      const total = cards.length
      const totalPages = Math.max(1, Math.ceil(total / per))
      const page = Math.min(Math.max(this.currentActivePositionsPage || 1, 1), totalPages)

      const start = (page - 1) * per
      const end = start + per

      return cards.slice(start, end)
    },
    totalCardsCount() {
      return this.activeTab === 0 ? this.rfqCards.length : this.operationCards.length
    },
    reconcileDealData() {
      if (!this.selectedDeal) return { message: null }
      return {
        message: {
          rfq_id: this.selectedDeal.rfq_id,
          bank_id: this.selectedDeal.bank_id ?? null,
          bank_name: this.selectedDeal.bank_name || '-',
          rfq_orderqty: this.selectedDeal.amount,
          rfq_side: this.selectedDeal.side,
          rfq_orderqty_ccy: this.selectedDeal.pair,
          rfq_px: this.selectedDeal.rfq_px,
          rfq_security: 'FXSPOT',
          partmaster_id: 0,
          operation_alias: this.selectedDeal.activeDealOp?.[0]?.alias ?? '',
          operation_type: this.selectedDeal.activeDealOp?.[0]?.operationType ?? '',
          broker_name: this.selectedDeal.bank_name || '',
          company_name: this.selectedDeal.client || '',
          company_cnpj: this.selectedDeal.client_cnpj || '',
        }
      }
    }
  },
  watch: {
    activeTab() {
      this.currentActivePositionsPage = 1
    },
  },
  methods: {
    isReconciled(deal){
      return Boolean(deal.rfq_reconcil_sign)
    },
    formatOriginalRate(deal){
      const pxo = Number(deal.rfq_reconcil_sign?.rfq_reconcil_pxo)
      return Number.isFinite(pxo) ? pxo.toFixed(4) : '-'
    },
    printRate(card){
      const price = Number(card.rfq_px)
      return Number.isFinite(price) ? price.toFixed(4) : '0.0000'
    },
    formatCurrencyBRL(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    formatCurrencyBRLCompactAbbr(val) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'BRL',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(val)

      return formatted
        .replace(/\s?mil/i, 'k')
        .replace(/\s?mi/i, 'M')
        .replace(/\s?bi/i, 'B')
    },
    formatCurrencyBRLCompactSigned(val) {
      const abs = Math.abs(val)
      const sign = val >= 0 ? '+' : '-'
      return `${sign}${this.formatCurrencyBRLCompactAbbr(abs)}`
    },
    getClassColor(val) {
      if (val > 0) return 'green--text text--darken-2'
      if (val < 0) return 'red--text text--darken-2'
      return ''
    },
    translateStatus(status, isOperation = false) {
      if (!status) return ''
      let s = status.toLowerCase().replace(/[\s_]+/g, '')
      if (isOperation) {
        if (s === 'deal') s = 'complete'
        if (s === 'pending') s = 'rfqpending'
      }
      const translated = this.$t(`broker.status.${s}`)
      if (translated.startsWith('broker.status.')) {
        return status.toUpperCase()
      }
      return translated.toUpperCase()
    },
    getStatusColor(status) {
      if (!status) return 'grey darken-1'
      const s = status.toLowerCase()
      if (s === 'active' || s === 'ativo') return 'green'
      if (s === 'deal' || s === 'negocio' || s === 'complete' || s === 'concluído' || s === 'concluido' || s === 'negociado') return 'green'
      if (s === 'paused' || s === 'pausado') return 'orange lighten-3'
      if (s === 'pending auth' || s === 'pendente autenticação') return 'blue lighten-4'
      if (s === 'pending' || s === 'pendente' || s === 'rfq pending' || s === 'rfq pendente' || s === 'cotação pendente' || s === 'cotacao pendente') return 'orange darken-2'
      if (s === 'aml_pending' || s === 'aml pending' || s === 'aml' || s === 'pld' || s === 'amlpending') return 'grey darken-1'
      if (s === 'quote' || s === 'cotado') return 'blue lighten-2'
      if (s === 'cancelled' || s === 'cancelado') return 'grey darken-1'
      console.warn('[ActivePositions] Unknown status:', status)
      return 'grey darken-1'
    },
    openReconcileModal(deal) {
      this.selectedDeal = deal
      this.reconcileModal = true
    },
    handleReconcileSubmit(data) {
      this.$emit('reconcileSubmit', data)
    },
    formatOperationTypeTitle(type) {
      if (!type) return '-'
      const operationTypes = this.$t('broker.operationTypes')
      return String(operationTypes?.[type] || type || '-')
    }
  }
}
</script>

<style scoped>
.active-rfqs {
  min-height: 100%;
}

.flex-gap-2 {
  gap: 8px;
}

.tabs-class {
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  margin: 0;
  padding: 0;
}
</style>
